import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { contextImportApi, formatApiError } from '../lib/api';
import { pollJob } from '../lib/pollJob';
import { WriteOnly } from '../components/ViewerGuard';
import { HelpButton } from '../components/HelpPanel';
import {
  Upload,
  Loader2,
  User,
  Building,
  ArrowRight,
  Check,
  CheckCircle,
  Square,
  CheckSquare,
  AlertCircle,
  Lightbulb,
  Sparkles,
  FileText,
  X,
  File as FileIcon,
  Image
} from 'lucide-react';

import usePageTitle from '../hooks/usePageTitle';
import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ContextImportPage = () => {
  usePageTitle('Import Context');
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [extracted, setExtracted] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileExtracting, setFileExtracting] = useState(false);
  const [fileText, setFileText] = useState('');
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // Checkbox selections
  const [selectedIndividuals, setSelectedIndividuals] = useState({});
  const [selectedOrgs, setSelectedOrgs] = useState({});
  const [selectedPipeline, setSelectedPipeline] = useState({});

  const [parseProgress, setParseProgress] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError(`Unsupported format. Supported: PDF, DOCX, TXT, JPG, PNG`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File too large — try uploading individual sections or paste a summary');
      return;
    }

    setUploadedFile(file);
    setFileError('');
    setFileExtracting(true);
    setFileText('');

    try {
      const res = await contextImportApi.extractFile(file);
      setFileText(res.data.text);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFileError(detail || 'Could not read this file — try copying and pasting the content directly');
      setUploadedFile(null);
    } finally {
      setFileExtracting(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFileText('');
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleParse = async () => {
    const combinedText = [fileText, rawText].filter(Boolean).join('\n\n--- Additional Context ---\n\n');
    if (!combinedText.trim()) return;
    setParsing(true);
    setError('');
    setExtracted(null);
    setImportResult(null);
    setParseProgress('Starting extraction...');

    try {
      // Start async parse job
      const startRes = await contextImportApi.parse(combinedText.trim());
      const jobId = startRes.data.job_id;

      if (!jobId) {
        // Backward compat: if response has extracted directly
        const data = startRes.data.extracted || startRes.data;
        if (data.error) { setError(data.error); return; }
        setExtracted(data);
        _selectAll(data);
        return;
      }

      // Poll for completion
      const capacityMsg = 'Import Capacity Maxxed — this content may be too long. Try pasting a summary or divide into sections and run multiple imports.';
      await pollJob({
        fetchStatus: () => contextImportApi.parseStatus(jobId).then(r => r.data),
        isComplete: (d) => d.status === 'complete' && d.result,
        isError: (d) => d.status === 'error' || d.status === 'expired',
        getError: (d) => {
          const isCapacity = /timeout|too long|too large|budget|exceeded|token|limit|capacity|chunk/i.test(d.error || '');
          return isCapacity ? capacityMsg : (d.error || 'Context parsing failed');
        },
        onProgress: (job) => setParseProgress(job.progress || `Processing chunk ${(job.chunks_done || 0) + 1} of ${job.chunks_total || '?'}...`),
        onComplete: (job) => { setExtracted(job.result); _selectAll(job.result); },
        onError: (msg) => setError(msg),
        intervalMs: 5000,
        maxPolls: 120,
      });
    } catch (err) {
      const status = err.response?.status;
      const isCapacity = status === 502 || status === 504 || status === 413 || /timeout|too long|too large/i.test(formatApiError(err));
      setError(isCapacity
        ? 'Import Capacity Maxxed — this content may be too long. Try pasting a summary or divide into sections and run multiple imports.'
        : formatApiError(err));
    } finally {
      setParsing(false);
      setParseProgress('');
    }
  };

  const _selectAll = (data) => {
    const indSel = {};
    (data.individuals || []).forEach((_, i) => { indSel[i] = true; });
    setSelectedIndividuals(indSel);
    const orgSel = {};
    (data.organisations || []).forEach((_, i) => { orgSel[i] = true; });
    setSelectedOrgs(orgSel);
    const pipSel = {};
    (data.pipeline_updates || []).forEach((_, i) => { pipSel[i] = true; });
    setSelectedPipeline(pipSel);
  };

  const handleConfirm = async () => {
    if (!extracted) return;
    setImporting(true);
    setError('');

    const payload = {
      individuals: (extracted.individuals || []).filter((_, i) => selectedIndividuals[i]),
      organisations: (extracted.organisations || []).filter((_, i) => selectedOrgs[i]),
      pipeline_updates: (extracted.pipeline_updates || []).filter((_, i) => selectedPipeline[i]),
    };

    try {
      const res = await contextImportApi.confirm(payload);
      setImportResult(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const toggleIndividual = (i) => setSelectedIndividuals(prev => ({ ...prev, [i]: !prev[i] }));
  const toggleOrg = (i) => setSelectedOrgs(prev => ({ ...prev, [i]: !prev[i] }));
  const togglePipeline = (i) => setSelectedPipeline(prev => ({ ...prev, [i]: !prev[i] }));

  const getStageLabel = (stage) => {
    const labels = {
      identified: 'Identified', researched: 'Researched', outreach_sent: 'Outreach Sent',
      response_received: 'Response', meeting_booked: 'Meeting Booked', proposal_sent: 'Proposal Sent',
      active: 'Active', closed_won: 'Won', closed_lost: 'Lost',
    };
    return labels[stage] || stage;
  };

  const getTempColor = (temp) => {
    const colors = { hot: 'text-red-400', warm: 'text-yellow-400', cold: 'text-blue-400', dormant: 'text-gray-400' };
    return colors[temp] || 'text-gray-400';
  };

  const selectedCount = () => {
    return Object.values(selectedIndividuals).filter(Boolean).length +
           Object.values(selectedOrgs).filter(Boolean).length +
           Object.values(selectedPipeline).filter(Boolean).length;
  };

  const handleReset = () => {
    setRawText('');
    setExtracted(null);
    setImportResult(null);
    setError('');
    removeFile();
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
            <Upload className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-heading text-3xl text-white" data-testid="context-import-title">Import Context</h1>
              <HelpButton />
              <HowDoesThisWorkPill feature="context_import" />
            </div>
            <p className="text-sm text-[#A3B5C7]/70">Paste any email, WhatsApp, or notes — BREACH extracts contacts, updates pipeline, and surfaces next actions automatically.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400" data-testid="context-import-error">
          {error}
        </div>
      )}

      {/* Import Success */}
      {importResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 rounded-lg bg-green-500/10 border border-green-500/30"
          data-testid="import-success"
        >
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-heading text-white">Import Complete</h3>
          </div>
          <p className="text-green-400 text-sm">
            Created {importResult.summary?.targets_created || 0} target(s) and {importResult.summary?.pipeline_records_created || 0} pipeline record(s).
          </p>
          <button onClick={handleReset} className="btn-primary mt-4 text-sm" data-testid="import-another-btn">
            Import Another
          </button>
        </motion.div>
      )}

      {/* Input Section */}
      {!importResult && (
        <>
          <div className="card-surface p-6 mb-6">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#00D4C8] mb-3">
              Paste Email, WhatsApp, Meeting Notes, or Claude Summary
            </label>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              className="w-full bg-[#0A1628] border border-[#1A2F4C] rounded-lg p-4 text-[#D4E2F0] leading-relaxed resize-none focus:border-[#00D4C8] focus:ring-2 focus:ring-[#00D4C8]/20 transition-colors"
              rows={8}
              placeholder="Paste your email thread, WhatsApp conversation, meeting notes, or any communication here..."
              data-testid="context-import-textarea"
            />

            {/* File Upload Area */}
            <div className="mt-3 space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-upload-input"
              />

              {!uploadedFile ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={fileExtracting}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[#1A2F4C] hover:border-[#00D4C8]/40 text-[#A3B5C7] hover:text-white transition-all text-sm"
                  data-testid="upload-file-btn"
                >
                  <FileIcon className="w-4 h-4" />
                  Upload File
                  <span className="text-[#A3B5C7]/50 text-xs ml-1">(PDF, DOCX, TXT, JPG, PNG)</span>
                </button>
              ) : (
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                  {uploadedFile.name.match(/\.(jpg|jpeg|png)$/i) ? (
                    <Image className="w-4 h-4 text-[#00D4C8] flex-shrink-0" />
                  ) : (
                    <FileText className="w-4 h-4 text-[#00D4C8] flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{uploadedFile.name}</p>
                    <p className="text-[#A3B5C7]/60 text-xs">
                      {formatFileSize(uploadedFile.size)}
                      {fileExtracting && ' — extracting text...'}
                      {fileText && ` — ${fileText.length.toLocaleString()} characters extracted`}
                    </p>
                  </div>
                  {fileExtracting ? (
                    <Loader2 className="w-4 h-4 text-[#00D4C8] animate-spin flex-shrink-0" />
                  ) : fileText ? (
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  ) : null}
                  <button
                    onClick={removeFile}
                    className="p-1 rounded hover:bg-white/10 text-[#A3B5C7] hover:text-white transition-colors flex-shrink-0"
                    data-testid="remove-file-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {fileError && (
                <div className="flex items-center gap-2 text-red-400 text-xs" data-testid="file-error">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {fileError}
                </div>
              )}
            </div>

            <p className="text-xs text-[#A3B5C7]/60 mt-2">You can paste notes, thoughts or observations in any format — BREACH will extract and apply the relevant intelligence.</p>
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-[#A3B5C7]">
                {(() => {
                  const totalLen = (fileText?.length || 0) + (rawText?.length || 0);
                  if (totalLen > 20000) return `${totalLen.toLocaleString()} characters — will process in ${Math.ceil(totalLen / 20000)} chunks`;
                  if (totalLen > 0) return `${totalLen.toLocaleString()} characters${fileText && rawText ? ' (file + text)' : ''}`;
                  return 'Supports any format — email, chat, notes, files, AI summaries';
                })()}
              </p>
              <button
                onClick={handleParse}
                disabled={parsing || (!rawText.trim() && !fileText.trim()) || fileExtracting}
                className="btn-primary flex items-center gap-2"
                data-testid="parse-context-btn"
              >
                {parsing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {parseProgress || 'Extracting...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                    Extract Data
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Loading state */}
          {parsing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Loader2 className="w-8 h-8 text-[#00D4C8] animate-spin mx-auto mb-4" />
              <p className="text-white">Claude is reading your communication...</p>
              <p className="text-xs text-[#A3B5C7] mt-1">Extracting contacts, organisations, and next actions</p>
            </motion.div>
          )}

          {/* Extracted Results */}
          <AnimatePresence>
            {extracted && !parsing && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
                data-testid="extracted-results"
              >
                {/* Key Insights */}
                {extracted.key_insights?.length > 0 && (
                  <div className="card-surface p-6">
                    <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#00D4C8] mb-3">
                      <Lightbulb className="w-4 h-4" /> Key Insights
                    </h3>
                    <ul className="space-y-2">
                      {extracted.key_insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#D4E2F0] text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00D4C8] mt-2 flex-shrink-0" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Individuals */}
                {extracted.individuals?.length > 0 && (
                  <div className="card-surface p-6" data-testid="extracted-individuals">
                    <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#00D4C8] mb-4">
                      <User className="w-4 h-4" /> Individuals ({extracted.individuals.length})
                    </h3>
                    <div className="space-y-3">
                      {extracted.individuals.map((person, i) => (
                        <div
                          key={i}
                          onClick={() => toggleIndividual(i)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedIndividuals[i]
                              ? 'bg-[#00D4C8]/5 border-[#00D4C8]/30'
                              : 'bg-[#0A1628] border-[#1A2F4C] opacity-50'
                          }`}
                          data-testid={`individual-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            {selectedIndividuals[i] ?
                              <CheckSquare className="w-5 h-5 text-[#00D4C8] flex-shrink-0 mt-0.5" /> :
                              <Square className="w-5 h-5 text-[#A3B5C7] flex-shrink-0 mt-0.5" />
                            }
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{person.name}</p>
                                {person.title && <span className="text-xs text-[#00D4C8]">{person.title}</span>}
                              </div>
                              {person.organisation && (
                                <p className="text-sm text-[#A3B5C7]">{person.organisation}</p>
                              )}
                              {person.notes && (
                                <p className="text-sm text-[#A3B5C7] mt-1">{person.notes}</p>
                              )}
                              {person.email && (
                                <p className="text-xs text-[#00D4C8] mt-1">{person.email}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Organisations */}
                {extracted.organisations?.length > 0 && (
                  <div className="card-surface p-6" data-testid="extracted-organisations">
                    <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#00D4C8] mb-4">
                      <Building className="w-4 h-4" /> Organisations ({extracted.organisations.length})
                    </h3>
                    <div className="space-y-3">
                      {extracted.organisations.map((org, i) => (
                        <div
                          key={i}
                          onClick={() => toggleOrg(i)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedOrgs[i]
                              ? 'bg-[#00D4C8]/5 border-[#00D4C8]/30'
                              : 'bg-[#0A1628] border-[#1A2F4C] opacity-50'
                          }`}
                          data-testid={`organisation-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            {selectedOrgs[i] ?
                              <CheckSquare className="w-5 h-5 text-[#00D4C8] flex-shrink-0 mt-0.5" /> :
                              <Square className="w-5 h-5 text-[#A3B5C7] flex-shrink-0 mt-0.5" />
                            }
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-white font-medium">{org.name}</p>
                                <span className="text-xs px-2 py-0.5 bg-white/10 text-[#A3B5C7] rounded">{org.type}</span>
                              </div>
                              {org.bd_work_types?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {org.bd_work_types.map((t, j) => (
                                    <span key={j} className="text-[10px] uppercase tracking-wide px-2 py-0.5 bg-[#00D4C8]/10 text-[#00D4C8] rounded">
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {org.notes && (
                                <p className="text-sm text-[#A3B5C7] mt-1">{org.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pipeline Updates */}
                {extracted.pipeline_updates?.length > 0 && (
                  <div className="card-surface p-6" data-testid="extracted-pipeline">
                    <h3 className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#00D4C8] mb-4">
                      <ArrowRight className="w-4 h-4" /> Pipeline Actions ({extracted.pipeline_updates.length})
                    </h3>
                    <div className="space-y-3">
                      {extracted.pipeline_updates.map((update, i) => (
                        <div
                          key={i}
                          onClick={() => togglePipeline(i)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedPipeline[i]
                              ? 'bg-[#00D4C8]/5 border-[#00D4C8]/30'
                              : 'bg-[#0A1628] border-[#1A2F4C] opacity-50'
                          }`}
                          data-testid={`pipeline-update-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            {selectedPipeline[i] ?
                              <CheckSquare className="w-5 h-5 text-[#00D4C8] flex-shrink-0 mt-0.5" /> :
                              <Square className="w-5 h-5 text-[#A3B5C7] flex-shrink-0 mt-0.5" />
                            }
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-white font-medium">{update.target_name}</p>
                                <span className="text-xs px-2 py-0.5 bg-[#00D4C8]/10 text-[#00D4C8] rounded">
                                  {getStageLabel(update.suggested_stage)}
                                </span>
                                <span className={`text-xs capitalize ${getTempColor(update.temperature)}`}>
                                  {update.temperature}
                                </span>
                              </div>
                              {update.next_actions?.length > 0 ? (
                                <div className="mt-1 space-y-1">
                                  {update.next_actions.map((na, j) => (
                                    <p key={j} className="text-sm text-white">
                                      <span className="text-[#A3B5C7]">Action {j+1}: </span>{na.action}
                                      {na.due_date && (
                                        <span className="text-yellow-400 ml-2">Due: {na.due_date}</span>
                                      )}
                                    </p>
                                  ))}
                                </div>
                              ) : update.next_action && (
                                <p className="text-sm text-white mt-1">
                                  <span className="text-[#A3B5C7]">Next: </span>{update.next_action}
                                  {update.next_action_due && (
                                    <span className="text-yellow-400 ml-2">Due: {update.next_action_due}</span>
                                  )}
                                </p>
                              )}
                              {update.notes && (
                                <p className="text-sm text-[#A3B5C7] mt-1">{update.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confirm Import */}
                <div className="flex items-center justify-between p-6 card-surface">
                  <p className="text-[#A3B5C7] text-sm">
                    {selectedCount()} item(s) selected for import
                  </p>
                  <button
                    onClick={handleConfirm}
                    disabled={importing || selectedCount() === 0}
                    className="btn-primary flex items-center gap-2"
                    data-testid="confirm-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" strokeWidth={2} />
                        Confirm Import ({selectedCount()})
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default ContextImportPage;
